import { useRef } from 'react';
import gsap from 'gsap';
import { useGSAP } from '@gsap/react';
import { ScrollTrigger } from 'gsap/dist/ScrollTrigger';
import './Footer.css';

gsap.registerPlugin(useGSAP, ScrollTrigger);

const exploreLinks = [
  ['Our stays', '/stay'],
  ['Local experiences', '/explore'],
  ['Our story', '/our-story'],
  ['Contact', '/contact'],
];
const planLinks = [
  ['Check availability', '/booking'],
  ['Booking questions', '/#good-to-know'],
  ['Contact your host', '/contact'],
];
const destinations = [
  ['Valdosta, GA', '/stay/valdosta'],
  ['St Mary, Jamaica', '/stay/st-mary'],
  ['Hillsboro, Oregon', '/stay/oregon'],
];

function FooterLinks({ title, links }) {
  return (
    <nav className="site-footer__column" aria-label={`${title} navigation`}>
      <h2>{title}</h2>
      <ul>
        {links.map(([label, href]) => <li key={label}><a href={href}>{label}</a></li>)}
      </ul>
    </nav>
  );
}

export default function Footer({ ready = true }) {
  const scope = useRef(null);

  useGSAP(() => {
    if (!ready) return;
    const media = gsap.matchMedia();
    media.add('(prefers-reduced-motion: no-preference)', () => {
      const select = gsap.utils.selector(scope);
      gsap.timeline({
        defaults: { duration: 0.7, ease: 'power3.out', clearProps: 'transform,opacity,visibility' },
        scrollTrigger: { trigger: scope.current, start: 'top 90%', once: true },
      })
        .from(select('.site-footer__brand, .site-footer__column'), { autoAlpha: 0, y: 22, stagger: 0.09 })
        .from(select('.site-footer__divider'), { scaleX: 0, transformOrigin: 'left center', duration: 0.65 }, '-=.25')
        .from(select('.site-footer__bottom'), { autoAlpha: 0, y: 12 }, '-=.15');
    });
    return () => media.revert();
  }, { scope, dependencies: [ready], revertOnUpdate: true });

  return (
    <footer ref={scope} className="site-footer">
      <div className="site-footer__inner">
        <div className="site-footer__grid">
          <div className="site-footer__brand">
            <img src={`${process.env.PUBLIC_URL}/images/logo.jpg`} alt="JamRock Escape" width="1536" height="1024" />
            <p>Jamaican soul. A place to<br />feel at home.</p>
            <div className="site-footer__socials" aria-label="Social media links">
              <a href="/#facebook" aria-label="Facebook">f</a>
              <a href="/#instagram" aria-label="Instagram">◎</a>
              <a href="/#youtube" aria-label="YouTube">▶</a>
            </div>
          </div>
          <FooterLinks title="Explore" links={exploreLinks} />
          <FooterLinks title="Plan your stay" links={planLinks} />
          <nav className="site-footer__column" aria-label="Our destinations navigation">
            <h2>Our destinations</h2>
            <ul>
              {destinations.map(([label, href]) => <li key={label}><a href={href}>{label}</a></li>)}
            </ul>
          </nav>
          <div className="site-footer__column site-footer__contact">
            <h2>Contact info</h2>
            <dl>
              <div><dt>Phone</dt><dd>To be provided</dd></div>
              <div><dt>Email</dt><dd><a href="mailto:support@jamrockescape.com">support@jamrockescape.com</a></dd></div>
              <div><dt>Valdosta property</dt><dd>309 Rosedale Place<br />Valdosta, GA 31602</dd></div>
            </dl>
          </div>
        </div>
        <div className="site-footer__divider" />
        <div className="site-footer__bottom">
          <p>© JamRock Escape</p>
          <p>Designed &amp; Developed by <a href="https://americanwebpartners.com/" target="_blank" rel="noopener noreferrer">American Web Partners</a></p>
          <p><a href="/privacy-policy">Privacy policy</a><span aria-hidden="true"> · </span><a href="/booking-terms">Booking terms</a></p>
        </div>
      </div>
    </footer>
  );
}
